import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { localStorageService } from "../services/LocalStorageService";
import {
	applyNullOrderingFromOptions,
	enrichSortStateFromOptions,
	keepOfferedSortEntries,
	type MultiSortOption,
	multiSortComparator,
	type SortingState,
} from "../utils/multiColumnSorting";

/**
 * Manages multi-column sort state (compatible with `SystemSortingButtonWithPopup`)
 * and persists it to localStorage per-user, per-table.
 *
 * Returns the current state, a persisting setter, and a `sortRows` function that
 * applies the current sort to an array of rows via `multiSortComparator`.
 *
 * When `sortingOptions` is provided, the hook enriches default + restored entries with
 * each option's `label` and `customSortFunction`. This is required because:
 *  - `JSON.stringify` drops `customSortFunction`, so localStorage round-trips lose it.
 *  - Callers usually pass a bare `defaultSortState` (no labels), so the popup's
 *    "Restore default" otherwise shows raw column keys instead of human labels.
 *
 * Intended for tables where built-in DataGrid sorting is disabled and this hook
 * owns sort ordering end-to-end.
 *
 * `dropUnofferedSorts`: whenever the offered axes change (and on restore), entries matching none of
 * `sortingOptions` are removed and the result saved — for a grid that offers some axes only in
 * some modes (see `keepOfferedSortEntries`).
 */
export function useMultiSort<T>(
	localStorageKey: string,
	defaultSortState: SortingState<T>[],
	sortingOptions?: MultiSortOption<T>[],
	// DEFAULT: off — an unlisted entry is a deliberate hidden tie-breaker on most grids, so only a grid
	// that asks has its sort pruned to the offered axes.
	{ dropUnofferedSorts }: { dropUnofferedSorts: boolean } = { dropUnofferedSorts: false },
) {
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";

	const enrichedDefaultSortState = useMemo(() => enrichSortStateFromOptions(defaultSortState, sortingOptions), [defaultSortState, sortingOptions]);

	const [sortState, setSortState] = useState<SortingState<T>[]>(() => {
		const stored = localStorageService.getTableSortingState<T>(localStorageKey, userId);
		const restored = stored ? enrichSortStateFromOptions(stored, sortingOptions) : enrichedDefaultSortState;
		return dropUnofferedSorts && sortingOptions ? keepOfferedSortEntries(restored, sortingOptions) : restored;
	});

	// `useAuth` populates `userInfo` asynchronously after a fresh page load. If this hook ran
	// before that — e.g. on a route mounted outside the post-auth provider tree, or any reload
	// scenario where the API call hasn't resolved yet — the initial useState read used userId="".
	// Once userId becomes valid, retry the localStorage restore once. No-op if nothing was saved.
	const initialUserIdHadValueRef = useRef(!!userId);
	useEffect(() => {
		if (initialUserIdHadValueRef.current || !userId) return;
		initialUserIdHadValueRef.current = true;
		const stored = localStorageService.getTableSortingState<T>(localStorageKey, userId);
		if (stored) setSortState(enrichSortStateFromOptions(stored, sortingOptions));
	}, [userId, localStorageKey, sortingOptions]);

	const setSortStateAndPersist: typeof setSortState = useCallback(
		(action) => {
			setSortState((prev) => {
				/**
				 * Callers can do:
				 * setSortState(newValue); // pass a value directly
				 * setSortState(prev => [...prev, newItem]); // pass an updater function that returns updated value (we must evaluate it)
				 */
				const next = typeof action === "function" ? action(prev) : action;
				if (userId) localStorageService.saveTableSortingState(localStorageKey, next, userId);
				return next;
			});
		},
		[localStorageKey, userId],
	);

	// Keyed on the offered axes' columns, not the array: callers commonly rebuild `sortingOptions` every
	// render, and only a change in WHICH axes are offered should prune.
	const sortingOptionsRef = useRef(sortingOptions);
	sortingOptionsRef.current = sortingOptions;
	const offeredSortKey = sortingOptions?.map((option) => (option.sortColumns ?? []).map(String).join(",")).join("|");
	useEffect(() => {
		const options = sortingOptionsRef.current;
		if (!dropUnofferedSorts || offeredSortKey === undefined || !options) return;
		setSortState((prev) => {
			const kept = keepOfferedSortEntries(prev, options);
			return kept.length === prev.length ? prev : kept;
		});
		// The saved sort is pruned from storage rather than from state: on open, the initializer has
		// already pruned the state, so state alone can't tell that the saved copy still holds the axis.
		if (!userId || !localStorageKey) return;
		const saved = localStorageService.getTableSortingState<T>(localStorageKey, userId);
		if (!saved) return;
		const keptSaved = keepOfferedSortEntries(saved, options);
		if (keptSaved.length !== saved.length) localStorageService.saveTableSortingState(localStorageKey, keptSaved, userId);
	}, [dropUnofferedSorts, offeredSortKey, localStorageKey, userId]);

	const sortRows = useCallback(
		(rows: T[]) => [...rows].sort(multiSortComparator(applyNullOrderingFromOptions(sortState, sortingOptions))),
		[sortState, sortingOptions],
	);

	return { sortState, setSortState: setSortStateAndPersist, defaultSortState: enrichedDefaultSortState, sortRows };
}
